'use client'

import {
  useArtifactCanvas,
  ArtifactCanvasBody,
  ArtifactCanvasFooterBar,
  ArtifactCanvasSaveActions,
  ArtifactFormSkeleton,
  Button,
  FormField,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SectionTitle,
  Textarea,
} from '@yeshuman/sdk'
import { useEffect } from 'react'

import type { Opportunity } from '../../hooks/use-opportunities'
import { useApplicationApplyFlow } from '../../hooks/use-application-apply-flow'
import { applicationCanvasSurface } from '../../lib/canvas-ui-surface'

import {
  answerString,
  answerStringArray,
  answerStringOrNumber,
} from './candidate-featured-jobs-types'

export type ApplicationApplyCanvasProps = {
  opportunity: Opportunity
  onClose: () => void
  onApplied?: () => void
}

export function ApplicationApplyCanvas({
  opportunity,
  onClose,
  onApplied,
}: ApplicationApplyCanvasProps) {
  const { setCanvasSurface } = useArtifactCanvas()
  const {
    applicationId,
    screeningQuestions,
    isLoadingQuestions,
    answers,
    coverLetter,
    setCoverLetter,
    isSubmitting,
    onAnswerChange,
    submit,
  } = useApplicationApplyFlow(opportunity, {
    onSuccess: () => {
      onApplied?.()
      onClose()
    },
  })

  useEffect(() => {
    if (!applicationId) return
    setCanvasSurface(applicationCanvasSurface(applicationId))
  }, [applicationId, setCanvasSurface])

  if (isLoadingQuestions) {
    return (
      <>
        <ArtifactCanvasBody className="min-h-0 flex-1 overflow-y-auto">
          <ArtifactFormSkeleton fields={4} ariaLabel="Loading application form" />
        </ArtifactCanvasBody>
        <ArtifactCanvasFooterBar
          footerClassName="shrink-0"
          actions={
            <Button type="button" variant="outline" onClick={onClose} disabled>
              Cancel
            </Button>
          }
        />
      </>
    )
  }

  return (
    <>
      <ArtifactCanvasBody className="min-h-0 flex-1 overflow-y-auto">
        <p className="text-sm text-muted-foreground">
          Submit your application for <strong>{opportunity.title}</strong> at{' '}
          <strong>{opportunity.organisation_name}</strong>.
        </p>

        <div className="space-y-4">
          <FormField htmlFor="apply-cover-letter" label="Cover letter (optional)">
            <Textarea
              id="apply-cover-letter"
              placeholder="Introduce yourself or highlight relevant experience..."
              value={coverLetter}
              onChange={(e) => setCoverLetter(e.target.value)}
              className="min-h-[100px]"
            />
          </FormField>
        </div>

        {screeningQuestions.length > 0 ? (
          <div className="space-y-4">
            <SectionTitle className="text-lg font-medium">Screening Questions</SectionTitle>

            {screeningQuestions.map((question) => (
              <div key={question.id} className="space-y-2">
                <Label className="text-sm font-medium">
                  {question.question_text}
                  {question.is_required ? <span className="ml-1 text-destructive">*</span> : null}
                </Label>

                {question.question_type === 'text' ? (
                  <Textarea
                    placeholder="Enter your answer..."
                    value={answerString(answers[question.id])}
                    onChange={(e) => onAnswerChange(question.id, e.target.value)}
                    className="min-h-[80px]"
                  />
                ) : null}

                {question.question_type === 'number' ? (
                  <Input
                    type="number"
                    placeholder="Enter a number..."
                    value={answerStringOrNumber(answers[question.id])}
                    onChange={(e) =>
                      onAnswerChange(question.id, Number.parseInt(e.target.value, 10) || 0)
                    }
                    min={question.config?.validation?.min_value}
                    max={question.config?.validation?.max_value}
                  />
                ) : null}

                {question.question_type === 'single_choice' && question.config?.options ? (
                  <Select
                    value={answerString(answers[question.id])}
                    onValueChange={(value) => onAnswerChange(question.id, value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select an option..." />
                    </SelectTrigger>
                    <SelectContent>
                      {question.config.options.map((option: string) => (
                        <SelectItem key={option} value={option}>
                          {option}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : null}

                {question.question_type === 'multiple_choice' && question.config?.options ? (
                  <div className="space-y-2">
                    {question.config.options.map((option: string) => (
                      <div key={option} className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id={`apply-${question.id}-${option}`}
                          checked={answerStringArray(answers[question.id]).includes(option)}
                          onChange={(e) => {
                            const currentAnswers = answerStringArray(answers[question.id])
                            if (e.target.checked) {
                              onAnswerChange(question.id, [...currentAnswers, option])
                            } else {
                              onAnswerChange(
                                question.id,
                                currentAnswers.filter((a: string) => a !== option),
                              )
                            }
                          }}
                          className="rounded"
                        />
                        <Label htmlFor={`apply-${question.id}-${option}`} className="text-sm">
                          {option}
                        </Label>
                      </div>
                    ))}
                  </div>
                ) : null}

                {question.question_type === 'boolean' ? (
                  <Select
                    value={
                      answers[question.id] === true
                        ? 'true'
                        : answers[question.id] === false
                          ? 'false'
                          : ''
                    }
                    onValueChange={(value) => onAnswerChange(question.id, value === 'true')}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select yes or no..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="true">Yes</SelectItem>
                      <SelectItem value="false">No</SelectItem>
                    </SelectContent>
                  </Select>
                ) : null}
              </div>
            ))}
          </div>
        ) : null}
      </ArtifactCanvasBody>

      <ArtifactCanvasFooterBar
        footerClassName="shrink-0"
        actionsClassName="gap-3"
        actions={
          <ArtifactCanvasSaveActions
            onClose={onClose}
            onSave={() => void submit()}
            isDirty
            isSaving={isSubmitting}
            saveLabel="Submit Application"
            savingLabel="Submitting…"
            closeDisabled={isSubmitting}
          />
        }
      />
    </>
  )
}
